import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { FiX, FiPlus, FiLoader } from "react-icons/fi";
import { useArchitectureTabs, ArchitectureTab } from "../../context/ArchitectureTabsContext";

// Icones des providers
import azureIcon from "../Assets/azure.svg";
import awsIcon from "../Assets/aws-svgrepo-com.svg";
import gcpIcon from "../Assets/gcp-svgrepo-com.svg";

const providerIcons: Record<string, string> = {
  azure: azureIcon,
  aws: awsIcon,
  gcp: gcpIcon,
  fabric: "/icons/fabric/fabric_48_color.svg",
};

interface TabBarProps {
  onNewArchitecture?: () => void;
}

export default function TabBar({ onNewArchitecture }: TabBarProps) {
  const { t } = useTranslation('canvas');
  const navigate = useNavigate();
  const { tabs, activeTabId, setActiveTab, closeTab, reorderTabs } = useArchitectureTabs();
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState<string | null>(null);

  const handleCloseTab = (e: React.MouseEvent, tab: ArchitectureTab) => {
    e.stopPropagation();

    if (tab.isDirty) {
      setConfirmClose(tab.id);
    } else {
      // Si c'est le dernier onglet, rediriger vers /projects
      if (tabs.length === 1) {
        closeTab(tab.id);
        navigate('/projects');
      } else {
        closeTab(tab.id);
      }
    }
  };

  const confirmCloseTab = (tabId: string) => {
    // Si c'est le dernier onglet, rediriger vers /projects
    if (tabs.length === 1) {
      closeTab(tabId);
      navigate('/projects');
    } else {
      closeTab(tabId);
    }
    setConfirmClose(null);
  };

  const cancelClose = () => {
    setConfirmClose(null);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    setDraggedTabId(tabId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetTabId: string) => {
    e.preventDefault();
    if (draggedTabId && draggedTabId !== targetTabId) {
      const fromIndex = tabs.findIndex(t => t.id === draggedTabId);
      const toIndex = tabs.findIndex(t => t.id === targetTabId);
      if (fromIndex !== -1 && toIndex !== -1) {
        reorderTabs(fromIndex, toIndex);
      }
    }
    setDraggedTabId(null);
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f1f5f9",
          borderBottom: "1px solid #e2e8f0",
          padding: "4px 8px 0 8px",
          gap: "2px",
          minHeight: 40,
          overflow: "hidden",
        }}
      >
        {/* Onglets */}
        <div
          style={{
            display: "flex",
            flex: 1,
            gap: "2px",
            alignItems: "center",
          }}
        >
          {tabs.map((tab) => (
            <div
              key={tab.id}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, tab.id)}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
                backgroundColor: activeTabId === tab.id ? "#fff" : "transparent",
                border: activeTabId === tab.id ? "1px solid #e2e8f0" : "1px solid transparent",
                borderBottom: activeTabId === tab.id ? "1px solid #fff" : "1px solid transparent",
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                marginBottom: -1,
                transition: "all 0.15s ease",
                minWidth: 120,
                maxWidth: 200,
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                if (activeTabId !== tab.id) {
                  e.currentTarget.style.backgroundColor = "#e2e8f0";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTabId !== tab.id) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              {/* Provider icon */}
              {tab.isLoading ? (
                <FiLoader size={16} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <img
                  src={providerIcons[tab.provider] || azureIcon}
                  alt={tab.provider}
                  style={{ width: 16, height: 16 }}
                />
              )}

              {/* Tab name with dirty indicator */}
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 13,
                  fontWeight: activeTabId === tab.id ? 600 : 400,
                  color: tab.error ? "#dc2626" : "#374151",
                }}
                title={tab.name}
              >
                {tab.isDirty && (
                  <span style={{ color: "#f59e0b", marginRight: 4 }}>*</span>
                )}
                {tab.name}
              </span>

              {/* Close button */}
              <button
                onClick={(e) => handleCloseTab(e, tab)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  border: "none",
                  background: "transparent",
                  borderRadius: 4,
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#fecaca";
                  e.currentTarget.style.color = "#dc2626";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#6b7280";
                }}
                title={t('tabBar.closeTab')}
              >
                <FiX size={14} />
              </button>
            </div>
          ))}

          {/* Bouton nouvelle architecture - à la fin des onglets */}
          {onNewArchitecture && (
            <button
              onClick={onNewArchitecture}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                border: "none",
                background: "transparent",
                borderRadius: 6,
                cursor: "pointer",
                color: "#6b7280",
                marginLeft: 4,
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e2e8f0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
              title={t('tabBar.newArchitecture')}
            >
              <FiPlus size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Modal de confirmation pour fermer un onglet avec modifications */}
      {confirmClose && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={cancelClose}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 12,
              padding: 24,
              maxWidth: 400,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: 18, color: "#111827" }}>
              {t('tabBar.unsavedChanges.title')}
            </h3>
            <p style={{ margin: "0 0 20px", color: "#6b7280", fontSize: 14 }}>
              {t('tabBar.unsavedChanges.message')}
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                onClick={cancelClose}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                {t('tabBar.unsavedChanges.cancel')}
              </button>
              <button
                onClick={() => confirmCloseTab(confirmClose)}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  background: "#dc2626",
                  color: "#fff",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {t('tabBar.unsavedChanges.closeWithoutSaving')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS pour l'animation de chargement */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
